import { useState, useEffect } from 'react'
import { TagIcon } from '@heroicons/react/24/solid'
import { useMutation } from '@tanstack/react-query'
import { handlePostRequest } from '../../../utils/api-handler.util'
import RespondSuccessModal from './RespondSuccessModal'
import Modal from '../../common/Modal'
import { useNavigate } from 'react-router-dom'
import { QueryKeys } from '../../../utils/queryKeyGenerator'
import productRequestTypes from '../../../constants/productRequestTypes'

const RespondBidModal = ({
  isOpen,
  onClose,
  requestId,
  productId,
  productPrice,
  productPriceText,
}) => {
  const [finalPrice, setFinalPrice] = useState('')
  const [formError, setFormError] = useState(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!isOpen) {
      setFinalPrice('')
      setFormError(null)
    }
  }, [isOpen])

  const mutation = useMutation({
    mutationFn: (bidData) => {
      return handlePostRequest(`retailers/bid-requests/respond`, bidData)
    },
    onSuccess: () => {
      setShowSuccess(true)
      QueryKeys.retailerBidRequests.invalidate(productRequestTypes.all.key, 1)
    },
  })

  const handleSubmit = async (event) => {
    event.preventDefault()
    setFormError(null)
    mutation.reset()

    const bidPayload = {
      bidRequestId: requestId,
      productId: productId,
      finalPrice: parseFloat(finalPrice),
    }

    mutation.mutateAsync(bidPayload)
  }

  if (!isOpen) {
    return null
  }

  if (showSuccess) {
    return (
      <RespondSuccessModal
        isOpen={showSuccess}
        onClose={() => {
          setShowSuccess(false)
          onClose(true)
          navigate(`/retailer/products/requests/${requestId}`)
        }}
      />
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col items-center text-center">
        <div className="p-3 bg-green-100 rounded-full mb-4">
          <TagIcon className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Respond Product Bidding</h2>
        <p className="text-sm text-gray-600 mb-6 px-4">
          Customers can submit Product Bidding to notify all retailers offering that product that
          they are interested in receiving bids.
        </p>
        <form onSubmit={handleSubmit} className="w-full space-y-4 text-left">
          <div className="flex justify-between">
            <div className=" flex gap-2 items-center">
              <label className="block text-sm font-medium text-gray-700 ">
                Original Starting Price:
              </label>
              <div className="text-black font-semibold">{productPriceText}</div>
            </div>
          </div>

          <div>
            <label htmlFor="finalPrice" className="block text-sm font-medium text-gray-700 mb-1">
              Your Best/Final Price (Ksh) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="finalPrice"
              value={finalPrice}
              onChange={(e) => setFinalPrice(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 bg-neutral-200-50"
              placeholder="Enter your final price"
              max={productPrice}
            />
          </div>
          {formError && <p className="text-sm text-red-600 text-center">{formError}</p>}

          <button
            type="submit"
            disabled={mutation.isPending}
            className={`w-full px-4 py-3 text-base font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-out ${
              mutation.isPending
                ? 'bg-neutral-200-400 cursor-not-allowed'
                : 'bg-[#E47F00] hover:opacity-90 focus:ring-[#E47F00]'
            }`}
          >
            {mutation.isPending ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </Modal>
  )
}

export default RespondBidModal
